/**
 * PlayerHUD — camera-fixed overlay showing player health, mana, and ability cooldowns.
 *
 * All elements use setScrollFactor(0) to remain fixed on screen.
 * Uses art-bible palette colors. No hardcoded strings — all text from locale.
 * Supports keyboard, mouse, and gamepad (display only — no interactive elements here).
 */

import * as Phaser from 'phaser';
import { FontManager } from './FontManager';
import {
  PLAYER_MAX_HEALTH,
  PLAYER_MAX_MANA,
} from '@game/shared';
import {
  HUD_BG,
  HUD_BORDER,
  HEALTH_BAR_FILL,
  HEALTH_BAR_BG,
  MANA_BAR_FILL,
  MANA_BAR_BG,
  HUD_TEXT_SECONDARY,
  PAL_RED,
  hexToCSS,
} from './palette';
import { getString } from './locale';

const HUD_DEPTH = 1000;

// Bar dimensions
const BAR_X = 8;
const HEALTH_BAR_Y = 8;
const MANA_BAR_Y = 26;
const BAR_WIDTH = 120;
const BAR_HEIGHT = 12;
const BAR_BORDER = 1;

// Low health threshold
const LOW_HEALTH_THRESHOLD = 0.25;

export class PlayerHUD {
  private scene: Phaser.Scene;

  // Health bar
  private healthBg!: Phaser.GameObjects.Rectangle;
  private healthFill!: Phaser.GameObjects.Rectangle;
  private healthLabel!: Phaser.GameObjects.Text;
  private healthBorder!: Phaser.GameObjects.Rectangle;

  // Mana bar
  private manaBg!: Phaser.GameObjects.Rectangle;
  private manaFill!: Phaser.GameObjects.Rectangle;
  private manaLabel!: Phaser.GameObjects.Text;
  private manaBorder!: Phaser.GameObjects.Rectangle;

  // Low health warning
  private lowHealthText!: Phaser.GameObjects.Text;
  private lowHealthPulse: Phaser.Tweens.Tween | null = null;

  // Floor indicator
  private floorLabel!: Phaser.GameObjects.Text;

  // Weakness debuff indicator (24h after fainting)
  private weaknessIcon!: Phaser.GameObjects.Rectangle;
  private weaknessLabel!: Phaser.GameObjects.Text;
  private weakenedUntil: number = 0;

  // State
  private currentHealth = PLAYER_MAX_HEALTH;
  private maxHealth = PLAYER_MAX_HEALTH;
  private currentMana = PLAYER_MAX_MANA;
  private maxMana = PLAYER_MAX_MANA;
  private dirty = true;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.createHealthBar();
    this.createManaBar();
    this.createLowHealthWarning();
    this.createFloorIndicator();
    this.createWeaknessIndicator();
  }

  private createHealthBar(): void {
    const x = BAR_X + BAR_WIDTH / 2;
    const y = HEALTH_BAR_Y + BAR_HEIGHT / 2;

    // Border
    this.healthBorder = this.scene.add.rectangle(
      x, y,
      BAR_WIDTH + BAR_BORDER * 2, BAR_HEIGHT + BAR_BORDER * 2,
      HUD_BORDER,
    );
    this.fixToCamera(this.healthBorder);

    // Background
    this.healthBg = this.scene.add.rectangle(x, y, BAR_WIDTH, BAR_HEIGHT, HEALTH_BAR_BG);
    this.fixToCamera(this.healthBg);

    // Fill
    this.healthFill = this.scene.add.rectangle(
      BAR_X + BAR_WIDTH / 2, y,
      BAR_WIDTH, BAR_HEIGHT,
      HEALTH_BAR_FILL,
    );
    this.fixToCamera(this.healthFill);

    // Label
    this.healthLabel = this.scene.add.text(BAR_X + 4, HEALTH_BAR_Y + 1, '', FontManager.style('hud-sm'));
    this.fixToCamera(this.healthLabel);
  }

  private createManaBar(): void {
    const x = BAR_X + BAR_WIDTH / 2;
    const y = MANA_BAR_Y + BAR_HEIGHT / 2;

    this.manaBorder = this.scene.add.rectangle(
      x, y,
      BAR_WIDTH + BAR_BORDER * 2, BAR_HEIGHT + BAR_BORDER * 2,
      HUD_BORDER,
    );
    this.fixToCamera(this.manaBorder);

    this.manaBg = this.scene.add.rectangle(x, y, BAR_WIDTH, BAR_HEIGHT, MANA_BAR_BG);
    this.fixToCamera(this.manaBg);

    this.manaFill = this.scene.add.rectangle(
      BAR_X + BAR_WIDTH / 2, y,
      BAR_WIDTH, BAR_HEIGHT,
      MANA_BAR_FILL,
    );
    this.fixToCamera(this.manaFill);

    this.manaLabel = this.scene.add.text(BAR_X + 4, MANA_BAR_Y + 1, '', FontManager.style('hud-sm'));
    this.fixToCamera(this.manaLabel);
  }

  private createLowHealthWarning(): void {
    this.lowHealthText = this.scene.add.text(400, 80, getString('hud.health.low_warning'),
      FontManager.style('title', { fontSize: FontManager.px(16), color: hexToCSS(PAL_RED), align: 'center' }),
    );
    this.lowHealthText.setOrigin(0.5, 0.5);
    this.lowHealthText.setAlpha(0);
    this.fixToCamera(this.lowHealthText);
  }

  private fixToCamera(obj: Phaser.GameObjects.GameObject & { setScrollFactor: (x: number, y?: number) => void; setDepth: (d: number) => void }): void {
    obj.setScrollFactor(0);
    obj.setDepth(HUD_DEPTH);
    // Route to UI camera so zoom doesn't affect HUD (AGE-1029)
    (this.scene as any).addToUI?.(obj);
  }

  /** Called by the active world scene when local player health changes. */
  setHealth(current: number, max: number): void {
    if (current === this.currentHealth && max === this.maxHealth) return;
    this.currentHealth = current;
    this.maxHealth = max;
    this.dirty = true;
  }

  /** Called by the active world scene when local player mana changes. */
  setMana(current: number, max: number): void {
    if (current === this.currentMana && max === this.maxMana) return;
    this.currentMana = current;
    this.maxMana = max;
    this.dirty = true;
  }

  /** Called every frame from the active world scene's update(). */
  update(): void {
    if (this.dirty) {
      this.refreshBars();
      this.dirty = false;
    }
    // Check weakness debuff every ~60 frames (once per second at 60fps).
    this.weaknessCheckCounter = ((this.weaknessCheckCounter ?? 0) + 1) % 60;
    if (this.weaknessCheckCounter === 0) {
      this.refreshWeaknessIndicator();
    }
  }
  private weaknessCheckCounter: number = 0;

  private refreshBars(): void {
    // Health bar fill width
    const healthRatio = Math.max(0, this.currentHealth / this.maxHealth);
    const healthWidth = Math.max(1, BAR_WIDTH * healthRatio);
    this.healthFill.width = healthWidth;
    this.healthFill.x = BAR_X + healthWidth / 2;
    this.healthLabel.setText(
      `${getString('hud.health.label')} ${Math.ceil(this.currentHealth)}/${this.maxHealth}`,
    );

    // Mana bar fill width
    const manaRatio = Math.max(0, this.currentMana / this.maxMana);
    const manaWidth = Math.max(1, BAR_WIDTH * manaRatio);
    this.manaFill.width = manaWidth;
    this.manaFill.x = BAR_X + manaWidth / 2;
    this.manaLabel.setText(
      `${getString('hud.mana.label')} ${Math.ceil(this.currentMana)}/${this.maxMana}`,
    );

    // Low health warning
    const isLow = healthRatio <= LOW_HEALTH_THRESHOLD && this.currentHealth > 0;
    if (isLow && !this.lowHealthPulse) {
      this.lowHealthText.setAlpha(1);
      this.lowHealthPulse = this.scene.tweens.add({
        targets: this.lowHealthText,
        alpha: { from: 1, to: 0.3 },
        duration: 500,
        yoyo: true,
        repeat: -1,
      });
    } else if (!isLow && this.lowHealthPulse) {
      this.lowHealthPulse.destroy();
      this.lowHealthPulse = null;
      this.lowHealthText.setAlpha(0);
    }
  }

  // updateCooldowns removed — combat ability system stripped in Phase B.

  /** Create the floor indicator (hidden until dungeon starts) */
  private createFloorIndicator(): void {
    // Position to the right of the health/mana bars
    const x = BAR_X + BAR_WIDTH + 16;
    const y = HEALTH_BAR_Y + 4;

    this.floorLabel = this.scene.add.text(x, y, '', FontManager.style('hint', {
      fontSize: '11px',
      color: hexToCSS(HUD_TEXT_SECONDARY),
    }));
    this.floorLabel.setScrollFactor(0);
    this.floorLabel.setDepth(HUD_DEPTH);
    this.floorLabel.setVisible(false);
    (this.scene as any).addToUI?.(this.floorLabel);
  }

  /** Update the floor indicator text */
  setFloor(currentFloor: number, totalFloors?: number): void {
    if (totalFloors !== undefined && totalFloors > 0) {
      this.floorLabel.setText(`F${currentFloor}/${totalFloors}`);
    } else {
      this.floorLabel.setText(`F${currentFloor}`);
    }
    this.floorLabel.setVisible(true);
  }

  /** Hide the floor indicator (e.g. when returning to lobby) */
  hideFloor(): void {
    this.floorLabel.setVisible(false);
  }

  /** Create the weakness debuff indicator (dimmed circle with "W"). */
  private createWeaknessIndicator(): void {
    const x = BAR_X + BAR_WIDTH + 40;
    const y = MANA_BAR_Y + BAR_HEIGHT / 2;

    this.weaknessIcon = this.scene.add.rectangle(x, y, 18, 18, 0x8b0000, 0.6);
    this.weaknessIcon.setStrokeStyle(1, 0x6c624a, 1);
    this.fixToCamera(this.weaknessIcon);
    this.weaknessIcon.setVisible(false);

    this.weaknessLabel = this.scene.add.text(x, y, 'W', FontManager.style('hud-sm', {
      fontSize: '11px',
      color: hexToCSS(PAL_RED),
    }));
    this.weaknessLabel.setOrigin(0.5, 0.5);
    this.fixToCamera(this.weaknessLabel);
    this.weaknessLabel.setVisible(false);

    // Read stored weakness timestamp
    this.loadWeaknessTimestamp();
  }

  /** Load weakness timestamp from localStorage and game registry. */
  private loadWeaknessTimestamp(): void {
    // Check game registry first (set by FaintOverlayScene on faint)
    const registryVal = this.scene.game.registry.get('faint:weakenedUntil') as number | undefined;
    if (registryVal && registryVal > Date.now()) {
      this.weakenedUntil = registryVal;
      return;
    }
    // Fall back to localStorage
    try {
      const stored = localStorage.getItem('faint:weakenedUntil');
      if (stored) {
        const ts = Number(stored);
        if (!isNaN(ts) && ts > Date.now()) {
          this.weakenedUntil = ts;
        }
      }
    } catch {
      // localStorage unavailable; non-critical.
    }
  }

  /** Update weakness indicator visibility based on current time. */
  private refreshWeaknessIndicator(): void {
    // Also check registry for newly-set values
    const registryVal = this.scene.game.registry.get('faint:weakenedUntil') as number | undefined;
    if (registryVal && registryVal > this.weakenedUntil) {
      this.weakenedUntil = registryVal;
    }

    const isWeakened = this.weakenedUntil > Date.now();
    this.weaknessIcon.setVisible(isWeakened);
    this.weaknessLabel.setVisible(isWeakened);

    if (!isWeakened && this.weakenedUntil > 0) {
      // Weakness expired; clean up
      this.weakenedUntil = 0;
      try { localStorage.removeItem('faint:weakenedUntil'); } catch { /* */ }
      this.scene.game.registry.remove('faint:weakenedUntil');
    }
  }

  destroy(): void {
    this.healthBorder.destroy();
    this.healthBg.destroy();
    this.healthFill.destroy();
    this.healthLabel.destroy();
    this.manaBorder.destroy();
    this.manaBg.destroy();
    this.manaFill.destroy();
    this.manaLabel.destroy();
    this.lowHealthText.destroy();
    this.floorLabel.destroy();
    this.weaknessIcon.destroy();
    this.weaknessLabel.destroy();
    if (this.lowHealthPulse) this.lowHealthPulse.destroy();
  }
}
